/**
 * PsePrefetchCta — W23-2 inline pre-fetch CTA for PSR Setup.
 *
 * Why this exists. The Public Sentiment Report (PSR) preflight panel in
 * the New Report wizard surfaces "MISSING — Not yet queried" rows when
 * the SED has never been hit by the public-sentiment orchestrator on
 * this site. Before this CTA the user's only recovery path was to
 * detour into Site Evidence and rebuild the SED — which then might or
 * might not run `publicSentimentOrchestrator`. This component lets them
 * fix the gap inline:
 *
 *  - Single click. `Pre-fetch sentiment now (~2 min)` button next to the
 *    missing row. On click, dispatches the canonical store action
 *    `refreshSedSections(siteId, ['publicSentiment'])` which routes
 *    through `topUpSiteSed` → `maybeFetchPublicSentiment` →
 *    `orchestratePublicSentiment` (GDELT 2.0, MuniCode/Granicus/
 *    Civic-Plus minutes, Regulations.gov/FERC eLibrary/NRC ADAMS).
 *
 *  - In-flight feedback. A spinner + cycling per-source progress text
 *    so the user has a clear signal something is happening for what
 *    can be a ~2 minute round-trip. The status text rotates through
 *    the three free sources (GDELT, council minutes, federal dockets)
 *    on a 4 s cadence — visual reassurance, NOT a true per-source
 *    progress hook (the orchestrator's fan-out is fire-and-forget
 *    from this surface; per-source rollup is in the
 *    `public-sentiment.refreshed` audit emit).
 *
 *  - 5-min per-site rate-limit. Uses `sessionStorage` keyed on
 *    `pse-prefetch.<siteId>.lastAt`. A second click within the window
 *    renders the button disabled with a tooltip showing minutes
 *    remaining. The session-scoped guard is intentional: it survives
 *    wizard re-mounts inside the same tab but resets on a fresh
 *    session so a user who returns the next day isn't blocked.
 *
 *  - Audit emit. On terminal state (ok / error / partial), fires the
 *    `public-sentiment.prefetched` audit event with `siteId`,
 *    `sectionIds`, `durationMs`, and an `outcome` discriminator.
 *    Distinct from `public-sentiment.refreshed` (emitted from inside
 *    the orchestrator on cache MISS) so the two events can be counted
 *    independently — a CTA click that lands on a fresh Firestore
 *    cache hit will NOT emit `public-sentiment.refreshed` but WILL
 *    emit `public-sentiment.prefetched`.
 *
 * Presentation-only inside the wizard surface — the wizard's existing
 * `site.sedVersion` reload effect (ReportPreferencesStep.tsx:592) will
 * re-load the SED + recompute preflight once the underlying store
 * action settles, so the row flips from `never-queried` → `fresh` /
 * `queried-empty` without this component owning the transition.
 *
 * Pack: docs/walkthrough-reports/2026-05-19_indepth_findings_interim.md (W23-2)
 */
import React from 'react';
import { Loader2, CheckCircle2, AlertTriangle, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { useStore } from '../../store';
import { emitAuditEvent } from '../../lib/auditLogClient';

/* ── Public types ───────────────────────────────────────────────────── */

/**
 * Internal state machine. Visible to tests via `data-state` on the
 * outer wrapper so transitions can be asserted without poking at the
 * DOM internals.
 *
 *  - `idle`    — initial state, button rendered.
 *  - `running` — refresh dispatched, spinner + rotating source label
 *                rendered, button disabled.
 *  - `success` — refresh resolved; the wizard's SED reload effect will
 *                pick up the new section, this component fades out the
 *                CTA in favour of a small confirmation chip.
 *  - `error`   — refresh threw OR returned without resolving the gap.
 *                The CTA returns to the button with an inline warning;
 *                a retry is one click away.
 */
export type PsePrefetchState = 'idle' | 'running' | 'success' | 'error';

export interface PsePrefetchCtaProps {
  /** Site whose SED the orchestrator should hit. Threaded through to the
   *  store's `refreshSedSections` action and the audit-event target. */
  siteId: string;
  /** Section ids the missing row(s) cover. `publicSentiment` is the only
   *  SED section currently routed by the orchestrator; `siteContext` is
   *  accepted for symmetry with the wizard's PSR Setup rows (it's not a
   *  `SedSectionId` today but the W23 / W24 work may surface it as a
   *  preflight row). The array is audit-event metadata so the activity
   *  tab can roll up which rows a given click rescued — kept as
   *  `string[]` rather than `SedSectionId[]` so the caller can pass any
   *  preflight row id without a brittle cast at every site. */
  missingSections: string[];
  /** Fired exactly once on a successful refresh. The wizard uses this to
   *  re-evaluate preflight gating. The handler is invoked AFTER the
   *  audit emit so tests can pin the order. */
  onSuccess?: () => void;
  /** Optional rate-limit override (ms). Defaults to 5 minutes. Tests pass
   *  `0` to bypass; production callers don't override. */
  rateLimitMs?: number;
}

/* ── Constants ──────────────────────────────────────────────────────── */

/** Default rate-limit between successive prefetch clicks on the same site. */
const DEFAULT_RATE_LIMIT_MS = 5 * 60 * 1000;

/** Cadence of the rotating "Querying X…" progress label, in ms. */
const SOURCE_ROTATION_MS = 4_000;

/** sessionStorage key prefix for the per-site last-fetch timestamp. */
const STORAGE_KEY_PREFIX = 'pse-prefetch.';

/**
 * Per-source progress labels for the in-flight state. The orchestrator's
 * dispatch is parallel (Promise.all), so these labels are a UX cue, not
 * a true progress hook — the user is told what we're doing, in rough
 * order of the orchestrator's three free sources. PSR-5d's commercial
 * sources (Brandwatch, Talkwalker) only fire in `'standalone-psr'` mode
 * so they're not in this rotation.
 */
const SOURCE_LABELS: readonly string[] = [
  'Querying GDELT 2.0…',
  'Querying council minutes (MuniCode / Granicus)…',
  'Querying federal dockets (Regulations.gov / FERC / ADAMS)…',
];

/* ── Helpers (exported for tests) ──────────────────────────────────── */

/** Compose the per-site rate-limit storage key. */
export function makePsePrefetchStorageKey(siteId: string): string {
  return `${STORAGE_KEY_PREFIX}${siteId}.lastAt`;
}

/**
 * Read the most-recent prefetch timestamp from `sessionStorage` for the
 * given site. Returns `null` when no record exists OR the stored value
 * isn't a finite number (defensive against a malformed key).
 *
 * sessionStorage is the right scope here: the rate-limit should follow
 * the user's tab session but reset on a fresh login (so a returning
 * user the next day isn't surprised by a blocked button). localStorage
 * would over-persist; in-memory would under-persist across wizard
 * re-mounts.
 */
export function readLastPrefetchAtMs(siteId: string): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(makePsePrefetchStorageKey(siteId));
    if (!raw) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/** Best-effort persist. Failures (private mode, quota) are swallowed —
 *  the rate-limit degrades to "no guard" rather than throwing. */
export function writeLastPrefetchAtMs(siteId: string, atMs: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(makePsePrefetchStorageKey(siteId), String(atMs));
  } catch {
    /* swallow — see docblock */
  }
}

/**
 * Compute remaining-window in ms, given the last-fetch timestamp + the
 * configured limit. Returns 0 when the rate-limit doesn't apply OR has
 * already elapsed. Pure for testability.
 */
export function rateLimitRemainingMs(
  lastAtMs: number | null,
  rateLimitMs: number,
  nowMs: number,
): number {
  if (rateLimitMs <= 0) return 0;
  if (lastAtMs == null) return 0;
  const elapsed = nowMs - lastAtMs;
  if (!Number.isFinite(elapsed) || elapsed < 0) return 0;
  const remaining = rateLimitMs - elapsed;
  return remaining > 0 ? remaining : 0;
}

/** Human-readable "Try again in N min" tooltip. Rounds UP so a 30 s
 *  remaining shows as "1 minute" instead of "0 minutes". */
export function formatRateLimitTooltip(remainingMs: number): string {
  const minutes = Math.max(1, Math.ceil(remainingMs / 60_000));
  return `Try again in ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/* ── Component ─────────────────────────────────────────────────────── */

export const PsePrefetchCta: React.FC<PsePrefetchCtaProps> = ({
  siteId,
  missingSections,
  onSuccess,
  rateLimitMs = DEFAULT_RATE_LIMIT_MS,
}) => {
  const refreshSedSections = useStore(s => s.refreshSedSections);

  const [state, setState] = React.useState<PsePrefetchState>('idle');
  /** Cycles through SOURCE_LABELS while `state === 'running'`. */
  const [sourceIdx, setSourceIdx] = React.useState(0);
  /** When non-null, the last-fetch ts was read on the most recent render
   *  cycle — used by the disabled / tooltip path. We keep it in state so
   *  a successful click can refresh the window without forcing a parent
   *  re-render. */
  const [lastAtMs, setLastAtMs] = React.useState<number | null>(
    () => readLastPrefetchAtMs(siteId),
  );
  /** Ticks once a minute while disabled so the "Try again in N min"
   *  tooltip recomputes without re-rendering on every keystroke. */
  const [, setTick] = React.useState(0);

  // Re-read the storage anchor whenever the siteId prop changes (the
  // wizard can swap sites without unmounting this row).
  React.useEffect(() => {
    setLastAtMs(readLastPrefetchAtMs(siteId));
  }, [siteId]);

  // Tick the disabled tooltip's "minutes remaining" once a minute so a
  // user staring at the button watches the countdown advance.
  React.useEffect(() => {
    if (state !== 'idle') return;
    const remaining = rateLimitRemainingMs(lastAtMs, rateLimitMs, Date.now());
    if (remaining <= 0) return;
    const t = window.setInterval(() => setTick(n => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, [state, lastAtMs, rateLimitMs]);

  // Rotate the in-flight source label every SOURCE_ROTATION_MS.
  React.useEffect(() => {
    if (state !== 'running') {
      setSourceIdx(0);
      return;
    }
    const t = window.setInterval(
      () => setSourceIdx(i => (i + 1) % SOURCE_LABELS.length),
      SOURCE_ROTATION_MS,
    );
    return () => window.clearInterval(t);
  }, [state]);

  // ── Click handler ────────────────────────────────────────────────
  const onClick = React.useCallback(async () => {
    if (state === 'running') return;
    const nowMs = Date.now();
    const remaining = rateLimitRemainingMs(lastAtMs, rateLimitMs, nowMs);
    if (remaining > 0) return;

    setState('running');
    setSourceIdx(0);
    const startedAt = nowMs;
    let outcome: 'ok' | 'error' = 'ok';
    try {
      await refreshSedSections(siteId, ['publicSentiment']);
    } catch {
      // refreshSedSections is total (never throws in production), but
      // defence-in-depth — a thrown error from the store action MUST
      // leave the CTA back in a clickable state for a retry.
      outcome = 'error';
    }
    // Stamp the rate-limit anchor BEFORE the terminal transition so a
    // user mashing the button while we're still rendering the success
    // chip doesn't slip through.
    writeLastPrefetchAtMs(siteId, nowMs);
    setLastAtMs(nowMs);

    // Audit emit BEFORE onSuccess so the activity-log row lands before
    // the wizard re-renders the preflight (tests pin this order).
    try {
      await emitAuditEvent({
        action: 'public-sentiment.prefetched',
        target: { type: 'site', id: siteId, label: 'PSR Setup CTA' },
        metadata: {
          sectionIds: missingSections,
          durationMs: Date.now() - startedAt,
          outcome,
        },
      });
    } catch {
      /* audit is best-effort */
    }

    if (outcome === 'ok') {
      setState('success');
      onSuccess?.();
    } else {
      setState('error');
    }
  }, [state, lastAtMs, rateLimitMs, refreshSedSections, siteId, missingSections, onSuccess]);

  // ── Render ───────────────────────────────────────────────────────
  const nowMs = Date.now();
  const remainingMs = rateLimitRemainingMs(lastAtMs, rateLimitMs, nowMs);
  const rateLimited = state === 'idle' && remainingMs > 0;
  const buttonDisabled = state === 'running' || rateLimited;
  const tooltip = rateLimited ? formatRateLimitTooltip(remainingMs) : undefined;

  if (state === 'success') {
    return (
      <span
        className="inline-flex items-center gap-1 type-body text-success font-semibold uppercase tracking-widest"
        data-testid="pse-prefetch-cta"
        data-state={state}
      >
        <CheckCircle2 className="w-3 h-3" aria-hidden />
        Fetched — preflight will refresh
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1.5"
      data-testid="pse-prefetch-cta"
      data-state={state}
    >
      <Button
        variant="outline"
        size="sm"
        className="h-6 type-body uppercase tracking-widest px-2"
        onClick={onClick}
        disabled={buttonDisabled}
        title={tooltip}
        data-testid="pse-prefetch-cta-button"
      >
        {state === 'running' ? (
          <>
            <Loader2 className="w-3 h-3 mr-1 animate-spin" aria-hidden />
            Pre-fetching…
          </>
        ) : (
          <>
            <Download className="w-3 h-3 mr-1" aria-hidden />
            Pre-fetch sentiment now (~2 min)
          </>
        )}
      </Button>
      {state === 'running' && (
        <span
          className="type-body text-text-secondary"
          data-testid="pse-prefetch-cta-source"
        >
          {SOURCE_LABELS[sourceIdx] ?? SOURCE_LABELS[0]}
        </span>
      )}
      {state === 'error' && (
        <span
          className="inline-flex items-center gap-1 type-body text-warning"
          data-testid="pse-prefetch-cta-error"
        >
          <AlertTriangle className="w-3 h-3" aria-hidden />
          Pre-fetch failed — retry or generate with partial data
        </span>
      )}
    </span>
  );
};
